import { sound } from './sound';

/**
 * Background music: two looping tracks, one for placing and one for the battle, crossfaded when the
 * phase changes. A track comes from the art pack when the manifest lists an audio file for it
 * (`"audio": { "music:build": "music_build.mp3", "music:battle": "music_battle.mp3" }`), otherwise a
 * small step sequencer plays a synthesised theme on the same WebAudio context as the effects, so the
 * game has music from the first build with zero files. Own mute flag, separate from effects.
 */
export type MusicTrack = 'build' | 'battle';

const MUSIC_KEY = 'holdline.music';
const VOLUME = 0.42;
const FADE_S = 0.7;

interface Playing {
  track: MusicTrack;
  gain: GainNode;
  stop: () => void;
  source: 'file' | 'synth';
}

/** One voice of the sequencer: which oscillator, how it decays, how loud. */
interface Voice {
  type: OscillatorType;
  decay: number;
  gain: number;
  /** Second detuned oscillator for width. */
  detune?: number;
}

const VOICES: Record<string, Voice> = {
  marimba: { type: 'triangle', decay: 0.35, gain: 0.5 },
  pluck: { type: 'square', decay: 0.18, gain: 0.16 },
  bass: { type: 'sine', decay: 0.45, gain: 0.55 },
  saw: { type: 'sawtooth', decay: 0.22, gain: 0.22, detune: 8 },
  pad: { type: 'triangle', decay: 1.6, gain: 0.12, detune: 5 },
};

/** Note names to Hz, equal temperament, A4 = 440. */
function hz(note: string): number {
  const m = /^([A-G])(#?)(\d)$/.exec(note);
  if (!m) return 440;
  const idx = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'].indexOf(m[1]! + m[2]!);
  const oct = Number(m[3]);
  return 440 * Math.pow(2, (idx - 9) / 12 + (oct - 4));
}

/** A pattern is a list of (step, voice, note) over a bar count; steps are 16ths. */
type Step = [step: number, voice: keyof typeof VOICES, note: string, len?: number];

interface Theme {
  bpm: number;
  bars: number;
  steps: Step[];
  /** Hi-hat / shaker pattern by 16th step, 1 = hit, 2 = accent. */
  hats: number[];
  /** Kick and snare by 16th step: 'k' | 's' | ''. */
  drums: string[];
}

/** Build phase: C major, 100 BPM, marimba melody over ukulele-ish plucks and an upright bass. Four bars. */
const BUILD: Theme = {
  bpm: 100,
  bars: 4,
  steps: [
    // bass, one note per beat
    ...([0, 4, 8, 12].map((s) => [s, 'bass', 'C2'] as Step)),
    ...([16, 20, 24, 28].map((s) => [s, 'bass', 'A1'] as Step)),
    ...([32, 36, 40, 44].map((s) => [s, 'bass', 'F2'] as Step)),
    ...([48, 52, 56, 60].map((s) => [s, 'bass', 'G2'] as Step)),
    // plucked chords on the off-beats
    ...([2, 6, 10, 14].flatMap((s) => [[s, 'pluck', 'E4'], [s, 'pluck', 'G4']] as Step[])),
    ...([18, 22, 26, 30].flatMap((s) => [[s, 'pluck', 'C4'], [s, 'pluck', 'E4']] as Step[])),
    ...([34, 38, 42, 46].flatMap((s) => [[s, 'pluck', 'A4'], [s, 'pluck', 'C5']] as Step[])),
    ...([50, 54, 58, 62].flatMap((s) => [[s, 'pluck', 'B4'], [s, 'pluck', 'D5']] as Step[])),
    // marimba melody
    [0, 'marimba', 'E5'], [3, 'marimba', 'G5'], [6, 'marimba', 'E5'], [10, 'marimba', 'C5'], [12, 'marimba', 'D5'],
    [16, 'marimba', 'C5'], [19, 'marimba', 'E5'], [22, 'marimba', 'A4'], [26, 'marimba', 'C5'], [28, 'marimba', 'B4'],
    [32, 'marimba', 'A4'], [35, 'marimba', 'C5'], [38, 'marimba', 'F5'], [42, 'marimba', 'E5'], [44, 'marimba', 'C5'],
    [48, 'marimba', 'D5'], [51, 'marimba', 'G5'], [54, 'marimba', 'B4'], [58, 'marimba', 'D5'], [60, 'marimba', 'E5'], [62, 'marimba', 'G5'],
    // soft pad on the chord changes
    [0, 'pad', 'C4', 16], [16, 'pad', 'A3', 16], [32, 'pad', 'F3', 16], [48, 'pad', 'G3', 16],
  ],
  hats: Array.from({ length: 64 }, (_, i) => (i % 4 === 2 ? 1 : 0)),
  drums: Array.from({ length: 64 }, (_, i) => (i % 8 === 0 ? 'k' : i % 8 === 4 ? 's' : '')),
};

/** Battle: A minor, 128 BPM, pulsing saw bass, staccato string stabs, marching snare. Four bars. */
const BATTLE: Theme = {
  bpm: 128,
  bars: 4,
  steps: [
    // pulsing bass, eighths
    ...(Array.from({ length: 16 }, (_, i) => [i * 2, 'saw', i < 8 ? 'A1' : 'A1'] as Step)),
    ...(Array.from({ length: 16 }, (_, i) => [32 + i * 2, 'saw', i < 8 ? 'F1' : 'G1'] as Step)),
    // string stabs
    [0, 'pluck', 'A4'], [0, 'pluck', 'C5'], [0, 'pluck', 'E5'],
    [6, 'pluck', 'A4'], [6, 'pluck', 'C5'], [6, 'pluck', 'E5'],
    [11, 'pluck', 'G4'], [11, 'pluck', 'B4'], [11, 'pluck', 'D5'],
    [16, 'pluck', 'A4'], [16, 'pluck', 'C5'], [16, 'pluck', 'E5'],
    [22, 'pluck', 'A4'], [22, 'pluck', 'C5'], [22, 'pluck', 'E5'],
    [28, 'pluck', 'E5'], [28, 'pluck', 'G5'], [28, 'pluck', 'B5'],
    [32, 'pluck', 'F4'], [32, 'pluck', 'A4'], [32, 'pluck', 'C5'],
    [38, 'pluck', 'F4'], [38, 'pluck', 'A4'], [38, 'pluck', 'C5'],
    [44, 'pluck', 'G4'], [44, 'pluck', 'B4'], [44, 'pluck', 'D5'],
    [50, 'pluck', 'G4'], [50, 'pluck', 'B4'], [50, 'pluck', 'D5'],
    [56, 'pluck', 'E5'], [56, 'pluck', 'G#5'], [56, 'pluck', 'B5'],
    [60, 'pluck', 'E5'], [60, 'pluck', 'G#5'], [60, 'pluck', 'B5'],
    // lead: short rising motif every bar
    [8, 'marimba', 'E5'], [9, 'marimba', 'A5'], [24, 'marimba', 'G5'], [25, 'marimba', 'B5'],
    [40, 'marimba', 'F5'], [41, 'marimba', 'A5'], [42, 'marimba', 'C6'], [58, 'marimba', 'E6'], [59, 'marimba', 'B5'],
    [0, 'pad', 'A3', 32], [32, 'pad', 'F3', 16], [48, 'pad', 'G3', 8], [56, 'pad', 'E3', 8],
  ],
  hats: Array.from({ length: 64 }, (_, i) => (i % 2 === 0 ? (i % 8 === 0 ? 2 : 1) : 0)),
  drums: Array.from({ length: 64 }, (_, i) => (i % 8 === 0 ? 'k' : i % 8 === 4 ? 's' : i % 16 === 14 ? 's' : '')),
};

const THEMES: Record<MusicTrack, Theme> = { build: BUILD, battle: BATTLE };

class MusicEngine {
  private mutedFlag: boolean;
  private playing: Playing | null = null;
  private wanted: MusicTrack | null = null;
  private files = new Map<MusicTrack, AudioBuffer | 'loading' | 'none'>();
  private manifestBase: string | null = null;
  private manifestAudio: Record<string, string> = {};
  private noise: AudioBuffer | null = null;

  constructor() {
    let m = false;
    try {
      m = localStorage.getItem(MUSIC_KEY) === '0';
    } catch {
      /* private mode */
    }
    this.mutedFlag = m;
    // Start the moment the effects engine's context is running; the first tap on the page unlocks it.
    sound.onReady(() => this.apply());
    document.addEventListener('visibilitychange', () => this.apply());
  }

  get muted(): boolean {
    return this.mutedFlag;
  }

  setMuted(m: boolean): void {
    this.mutedFlag = m;
    try {
      localStorage.setItem(MUSIC_KEY, m ? '0' : '1');
    } catch {
      /* fine */
    }
    this.apply();
  }

  toggle(): boolean {
    this.setMuted(!this.mutedFlag);
    return !this.mutedFlag;
  }

  /** Point the engine at a pack; audio entries are loaded lazily the first time a track is wanted. */
  usePack(base: string, audio: Record<string, string> | undefined): void {
    this.manifestBase = base;
    this.manifestAudio = audio ?? {};
  }

  /** Which theme should be playing now; null stops with a fade (end screens use the stings instead). */
  setTrack(track: MusicTrack | null): void {
    this.wanted = track;
    this.apply();
  }

  get current(): MusicTrack | null {
    return this.playing?.track ?? null;
  }

  /** Whether the playing track comes from a pack file or the built-in sequencer. */
  get source(): 'file' | 'synth' | null {
    return this.playing?.source ?? null;
  }

  private apply(): void {
    const ctx = sound.context();
    const bus = sound.bus();
    if (!ctx || !bus) {
      // Not unlocked yet: try again when it is.
      sound.onReady(() => this.apply());
      return;
    }
    const want = this.mutedFlag || document.hidden ? null : this.wanted;
    if (this.playing && this.playing.track === want) return;
    if (this.playing) {
      const old = this.playing;
      this.playing = null;
      const t = ctx.currentTime;
      old.gain.gain.cancelScheduledValues(t);
      old.gain.gain.setValueAtTime(old.gain.gain.value, t);
      old.gain.gain.linearRampToValueAtTime(0, t + FADE_S);
      setTimeout(old.stop, FADE_S * 1000 + 50);
    }
    if (!want) return;
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0, ctx.currentTime);
    gain.gain.linearRampToValueAtTime(VOLUME, ctx.currentTime + FADE_S);
    gain.connect(bus);
    const file = this.fileFor(want);
    const stop = file instanceof AudioBuffer ? this.startFile(ctx, file, gain) : this.startSequencer(ctx, THEMES[want], gain);
    this.playing = { track: want, gain, stop, source: file instanceof AudioBuffer ? 'file' : 'synth' };
  }

  /** The pack's audio for a track once decoded; kicks off the load and returns null until then. */
  private fileFor(track: MusicTrack): AudioBuffer | null {
    const state = this.files.get(track);
    if (state instanceof AudioBuffer) return state;
    if (state === 'loading' || state === 'none') return null;
    const name = this.manifestAudio[`music:${track}`];
    if (!name || !this.manifestBase) {
      this.files.set(track, 'none');
      return null;
    }
    this.files.set(track, 'loading');
    const ctx = sound.context();
    if (!ctx) return null;
    fetch(`${this.manifestBase}${name}`)
      .then((r) => (r.ok ? r.arrayBuffer() : Promise.reject(new Error(String(r.status)))))
      .then((buf) => ctx.decodeAudioData(buf))
      .then((decoded) => {
        this.files.set(track, decoded);
        // Swap the synthesised stand-in for the real file if this track is still wanted.
        if (this.playing && this.playing.track === track) {
          const p = this.playing;
          this.playing = null;
          p.stop();
          this.apply();
        }
      })
      .catch(() => this.files.set(track, 'none'));
    return null;
  }

  private startFile(ctx: AudioContext, buf: AudioBuffer, gain: GainNode): () => void {
    const src = ctx.createBufferSource();
    src.buffer = buf;
    src.loop = true;
    src.connect(gain);
    src.start();
    return () => {
      try {
        src.stop();
      } catch {
        /* already stopped */
      }
      src.disconnect();
      gain.disconnect();
    };
  }

  /** Schedules the theme bar by bar a little ahead of the clock; stops when the returned function runs. */
  private startSequencer(ctx: AudioContext, theme: Theme, gain: GainNode): () => void {
    const stepS = 60 / theme.bpm / 4;
    const loopSteps = theme.bars * 16;
    let nextLoopAt = ctx.currentTime + 0.05;
    let alive = true;
    const noise = this.noiseBuffer(ctx);
    const scheduleLoop = (at: number): void => {
      for (const [step, voiceName, note, len] of theme.steps) {
        const v = VOICES[voiceName]!;
        const t = at + step * stepS;
        const dur = len ? len * stepS : v.decay;
        this.note(ctx, gain, v, hz(note), t, dur);
      }
      theme.hats.forEach((h, i) => {
        if (!h) return;
        this.hat(ctx, gain, noise, at + i * stepS, h === 2 ? 0.16 : 0.08);
      });
      theme.drums.forEach((d, i) => {
        if (d === 'k') this.kick(ctx, gain, at + i * stepS);
        else if (d === 's') this.snare(ctx, gain, noise, at + i * stepS);
      });
    };
    const loopS = loopSteps * stepS;
    const tick = (): void => {
      if (!alive) return;
      while (nextLoopAt < ctx.currentTime + 1.0) {
        scheduleLoop(nextLoopAt);
        nextLoopAt += loopS;
      }
    };
    tick();
    const timer = setInterval(tick, 250);
    return () => {
      alive = false;
      clearInterval(timer);
      setTimeout(() => gain.disconnect(), 100);
    };
  }

  private note(ctx: AudioContext, out: GainNode, v: Voice, f: number, at: number, dur: number): void {
    const env = ctx.createGain();
    env.gain.setValueAtTime(0.0001, at);
    env.gain.exponentialRampToValueAtTime(v.gain, at + 0.008);
    env.gain.exponentialRampToValueAtTime(0.0001, at + dur);
    env.connect(out);
    const oscs = v.detune ? [-v.detune, v.detune] : [0];
    for (const d of oscs) {
      const o = ctx.createOscillator();
      o.type = v.type;
      o.frequency.setValueAtTime(f, at);
      o.detune.setValueAtTime(d, at);
      o.connect(env);
      o.start(at);
      o.stop(at + dur + 0.05);
    }
  }

  private kick(ctx: AudioContext, out: GainNode, at: number): void {
    const o = ctx.createOscillator();
    o.type = 'sine';
    o.frequency.setValueAtTime(140, at);
    o.frequency.exponentialRampToValueAtTime(45, at + 0.12);
    const env = ctx.createGain();
    env.gain.setValueAtTime(0.7, at);
    env.gain.exponentialRampToValueAtTime(0.0001, at + 0.22);
    o.connect(env);
    env.connect(out);
    o.start(at);
    o.stop(at + 0.25);
  }

  private snare(ctx: AudioContext, out: GainNode, noise: AudioBuffer, at: number): void {
    const src = ctx.createBufferSource();
    src.buffer = noise;
    const flt = ctx.createBiquadFilter();
    flt.type = 'bandpass';
    flt.frequency.value = 1800;
    const env = ctx.createGain();
    env.gain.setValueAtTime(0.35, at);
    env.gain.exponentialRampToValueAtTime(0.0001, at + 0.16);
    src.connect(flt);
    flt.connect(env);
    env.connect(out);
    src.start(at);
    src.stop(at + 0.2);
  }

  private hat(ctx: AudioContext, out: GainNode, noise: AudioBuffer, at: number, gain: number): void {
    const src = ctx.createBufferSource();
    src.buffer = noise;
    const flt = ctx.createBiquadFilter();
    flt.type = 'highpass';
    flt.frequency.value = 7000;
    const env = ctx.createGain();
    env.gain.setValueAtTime(gain, at);
    env.gain.exponentialRampToValueAtTime(0.0001, at + 0.05);
    src.connect(flt);
    flt.connect(env);
    env.connect(out);
    src.start(at);
    src.stop(at + 0.08);
  }

  private noiseBuffer(ctx: AudioContext): AudioBuffer {
    if (this.noise) return this.noise;
    const len = Math.floor(ctx.sampleRate * 0.5);
    this.noise = ctx.createBuffer(1, len, ctx.sampleRate);
    const d = this.noise.getChannelData(0);
    for (let i = 0; i < len; i++) d[i] = Math.random() * 2 - 1;
    return this.noise;
  }
}

export const music = new MusicEngine();
